import { offlineDb } from './offline-db';
import { isOfflineCapableNative } from '../commons/utils/platform';
import { StockGroup } from '../modules/inventory/model/inventory.model';

// Two offline square-tally rows drawing from the same product/origin/quality/site pool could
// both "see" the same pre-existing server snapshot as available, since the offline stock cache
// (InventoryLotService.getStockGroupsCached) has no idea what's already been locally reserved
// by other not-yet-synced rows in this same offline session — each row's own reservation only
// becomes visible to the SERVER once its owning TU's CREATE_TU (and dependent SAVE_TALLY_DETAILS)
// actually sync. This computes "how much of the cached available figure has this device already
// promised to something else offline", so callers can subtract it before showing/validating
// availability — same idea for the TU form's stock badge, the Stock Picker, and the offline
// stock-shortage check, so it's centralized here once instead of three slightly-different copies.
//
// Scope: only rows queued against a still-provisional (not-yet-synced) TU are considered —
// already-synced TUs can't have offline row saves at all (TallysheetSquareListComponent/
// TallysheetListRoundComponent block Save offline for those, see offlineEditBlocked()), so
// offlineDb.pendingRows is the complete set of "reserved but the server doesn't know yet" rows.
export interface RowStockFilter {
  productId: number;
  siteId?: number;
  originCode?: string;
  qualityCode?: string;
  /** Excludes this TU's own queued rows from the reservation sum — for a call site that's
   *  computing "how much is left for ME" (the TU form badge, and the post-save stock-shortage
   *  check), the caller's own not-yet-synced rows must not count as competing reservations
   *  against itself, or every provisional TU with saved rows would see its own available stock
   *  shrink every time it re-checks — same idea as excludeTallyMasterId's server-side role, just
   *  for the offline/local side of the calculation. Pass this TU's own `_offlineClientUuid`. */
  excludeClientUuid?: string;
}

export async function getLocallyReservedCbm(filter: RowStockFilter): Promise<number> {
  if (!isOfflineCapableNative()) return 0;

  const pendingEntries = await offlineDb.pendingRows.toArray();
  if (!pendingEntries.length) return 0;

  // Resolve each pendingRows entry's owning TU siteId once — pendingRows itself only stores
  // raw tally rows, not which site their TU belongs to. Same tu:id: prefix scan (indexed, not
  // a full-table .filter()) used by view-transport-unit.component.ts's own pending-row lookup.
  const tuDetailEntries = await offlineDb.detailCache.where('key').startsWith('tu:id:').toArray();
  const siteIdByClientUuid = new Map<string, number | undefined>();
  for (const entry of tuDetailEntries) {
    const data = entry.data as any;
    const clientUuid = data?._offlineClientUuid;
    if (clientUuid) siteIdByClientUuid.set(clientUuid, data?.loadingSite?.loadingSiteId ?? data?.loadingSite?.id);
  }

  const originLc = (filter.originCode || '').toLowerCase();
  const qualityLc = (filter.qualityCode || '').toLowerCase();
  let reservedCbm = 0;

  for (const entry of pendingEntries) {
    if (filter.excludeClientUuid && entry.clientUuid === filter.excludeClientUuid) continue;
    const siteId = siteIdByClientUuid.get(entry.clientUuid);
    if (filter.siteId != null && siteId != null && siteId !== filter.siteId) continue;
    for (const row of (entry.rows as any[])) {
      if (row?.productId !== filter.productId) continue;
      const rowOriginLc = (row.originCode || '').toLowerCase();
      const rowQualityLc = (row.qualityCode || row.quality || '').toLowerCase();
      if (originLc && rowOriginLc && originLc !== rowOriginLc) continue;
      if (qualityLc && rowQualityLc && qualityLc !== rowQualityLc) continue;
      reservedCbm += row.netCbm || 0;
    }
  }

  return reservedCbm;
}

// Shared by every call site that shows a StockGroup[] bucket list (TU form badge, Stock Picker,
// breakdown popover) so the "subtract what this device has already promised offline" step is
// applied identically everywhere, instead of three near-duplicate copies of the same loop.
// Pieces aren't tracked per-row today (only netCbm is), so availablePieces is left as-is.
export async function applyLocalReservationsToGroups(groups: StockGroup[], excludeClientUuid?: string): Promise<StockGroup[]> {
  if (!groups.length) return groups;
  return Promise.all(groups.map(async (g) => {
    const reserved = await getLocallyReservedCbm({
      productId: g.productId, siteId: g.siteId,
      originCode: g.originCode ?? undefined, qualityCode: g.qualityCode ?? undefined,
      excludeClientUuid,
    });
    if (!reserved) return g;
    return { ...g, availableCbm: Math.max(0, (g.availableCbm || 0) - reserved) };
  }));
}
